import { useMemo, useState } from 'react';

import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableRow } from '@/components/table/DataTable';
import { TablePager } from '@/components/table/TablePager';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AI_CALL_STATUSES, AI_PROVIDERS, aiApi, costLabel, durationLabel, num, sinceFrom } from '@/lib/aiAdmin';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatCount, formatDateTime } from '@/lib/format';

import { CENTER, TITLE, columns, Loading, useCursorList } from './common';

const ANY = 'any';
const SINCE = [
  { id: '1h', label: 'Last hour' },
  { id: '24h', label: 'Last 24 hours' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
] as const;

function Pick({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (value: string) => void }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger size="sm" className="h-9 w-36" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>Any {label.toLowerCase()}</SelectItem>
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** The latest calls. A row has ids and numbers only: ai stores no content. */
export function CallsTab() {
  const [text, setText] = useState({ organizationId: '', modelId: '', routeId: '' });
  const [provider, setProvider] = useState(ANY);
  const [status, setStatus] = useState(ANY);
  const [since, setSince] = useState<(typeof SINCE)[number]['id']>('24h');
  const debounced = useDebouncedValue(text);
  // The start of the range is fixed when the filter is set, so Next sends the same `from` as the page that gave its token.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- a new filter value picks a new start on purpose
  const from = useMemo(() => sinceFrom(since), [since, debounced, provider, status]);
  const list = useCursorList(
    async (token) => {
      const page = await aiApi.calls(
        {
          ...debounced,
          provider: provider === ANY ? undefined : provider,
          status: status === ANY ? undefined : status,
          from,
          pageSize: '50',
        },
        token,
      );
      return { rows: page.calls ?? [], next: page.nextPageToken };
    },
    [debounced, provider, status, since, from],
  );

  const field = (name: keyof typeof text, label: string) => (
    <Input value={text[name]} onChange={(event) => setText({ ...text, [name]: event.target.value })} placeholder={label} aria-label={label} className="h-9 w-40" />
  );

  return (
    <DataTable
      toolbar={
        <>
          {field('organizationId', 'Organization')}
          {field('modelId', 'Model')}
          {field('routeId', 'Route')}
          <Pick label="Provider" value={provider} options={AI_PROVIDERS} onChange={setProvider} />
          <Pick label="Status" value={status} options={AI_CALL_STATUSES} onChange={setStatus} />
          <Select value={since} onValueChange={(value) => setSince(value as typeof since)}>
            <SelectTrigger size="sm" className="h-9 w-40" aria-label="Time">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SINCE.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      }
      footer={<TablePager {...list.pager} />}
    >
      <DataTableHead columns={columns(['Time', 'Status', 'Route', 'Tokens in / out', 'Duration'])} />
      <tbody>
        {(list.error ? [] : list.rows).map((call) => (
          <DataTableRow key={call.id}>
            <DataTableCell className={TITLE}>
              {formatDateTime(call.createdAt)}
              <span className="mt-0.5 block text-xs font-normal text-muted-foreground">{call.organizationId || '—'}</span>
            </DataTableCell>
            <DataTableCell className={CENTER}>{call.status || '—'}</DataTableCell>
            <DataTableCell className={CENTER}>
              {call.routeId || '—'}
              {call.reason ? <span className="mt-0.5 block text-xs text-muted-foreground">{call.reason}</span> : null}
            </DataTableCell>
            <DataTableCell className={`${CENTER} tabular-nums`}>
              {formatCount(num(call.usage?.inputTokens))} / {formatCount(num(call.usage?.outputTokens))}
              <span className="mt-0.5 block text-xs text-muted-foreground">{costLabel(call.costUsd)}</span>
            </DataTableCell>
            <DataTableCell className={`${CENTER} tabular-nums`}>{durationLabel(call.latencyMs)}</DataTableCell>
          </DataTableRow>
        ))}
        {list.loading && list.rows.length === 0 && !list.error ? (
          <tr>
            <td colSpan={5}>
              <Loading label="Loading calls" />
            </td>
          </tr>
        ) : null}
        {!list.loading && !list.error && list.rows.length === 0 ? (
          <tr>
            <td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">
              No calls match these filters.
            </td>
          </tr>
        ) : null}
      </tbody>
      {list.error ? <DataTableLoadError colSpan={5} message={list.error} onRetry={list.reload} /> : null}
    </DataTable>
  );
}
